import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
  CardActions,
  Snackbar,
  Alert,
  Divider,
  TextField,
  FormGroup,
  Checkbox,
} from '@mui/material';
import {
  Refresh,
  Email,
  NotificationsActive,
  NotificationsOff
} from '@mui/icons-material';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface NotificationRecipient {
  id: number;
  name: string;
  email: string;
  role?: string;
}

export interface CaseReminderConfig {
  reminder_types: {
    due_soon: boolean;
    overdue: boolean;
    rush_unworked: boolean;
    response_expected: boolean;
  };
  due_soon_days: number;
  rush_unworked_hours: number;
  response_expected_hours: number;
  include_case_notifications_recipients: boolean;
  admin_recipient_types: {
    overdue: boolean;
    rush_unworked: boolean;
  };
}

interface EmailSetting {
  id?: number;
  notification_type: string;
  recipient_user_ids: number[];
  is_active: boolean;
  config?: CaseReminderConfig;
}

interface EmailSettingsData {
  settings: EmailSetting[];
  notification_types: { [key: string]: string };
  recipients: NotificationRecipient[];
  case_reminder_defaults?: CaseReminderConfig;
  pmp_admins?: NotificationRecipient[];
}

const DEFAULT_CASE_REMINDER_CONFIG: CaseReminderConfig = {
  reminder_types: {
    due_soon: true,
    overdue: true,
    rush_unworked: true,
    response_expected: true,
  },
  due_soon_days: 3,
  rush_unworked_hours: 24,
  response_expected_hours: 48,
  include_case_notifications_recipients: true,
  admin_recipient_types: {
    overdue: true,
    rush_unworked: true,
  },
};

const mergeCaseReminderConfig = (
  config?: Partial<CaseReminderConfig>,
  defaults: CaseReminderConfig = DEFAULT_CASE_REMINDER_CONFIG
): CaseReminderConfig => ({
  reminder_types: {
    ...defaults.reminder_types,
    ...(config?.reminder_types ?? {}),
  },
  due_soon_days: config?.due_soon_days ?? defaults.due_soon_days,
  rush_unworked_hours: config?.rush_unworked_hours ?? defaults.rush_unworked_hours,
  response_expected_hours: config?.response_expected_hours ?? defaults.response_expected_hours,
  include_case_notifications_recipients:
    config?.include_case_notifications_recipients ?? defaults.include_case_notifications_recipients,
  admin_recipient_types: {
    ...defaults.admin_recipient_types,
    ...(config?.admin_recipient_types ?? {}),
  },
});

const EmailSettings: React.FC = () => {
  const { user } = useAuth();
  const [data, setData] = useState<EmailSettingsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [caseReminderDraft, setCaseReminderDraft] = useState<CaseReminderConfig>(DEFAULT_CASE_REMINDER_CONFIG);

  const isPmpAdmin = () => {
    if (!user?.roles) return false;
    return user.roles.some(role => role.slug === 'pmp_admin');
  };

  const caseReminderDefaults = useMemo(
    () => mergeCaseReminderConfig(data?.case_reminder_defaults),
    [data?.case_reminder_defaults]
  );

  useEffect(() => {
    fetchEmailSettings();
  }, []);

  const fetchEmailSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/email-settings');
      const payload = response.data.data as EmailSettingsData;
      setData(payload);

      const caseSetting = payload.settings.find((s) => s.notification_type === 'case_work_reminders');
      setCaseReminderDraft(mergeCaseReminderConfig(caseSetting?.config, mergeCaseReminderConfig(payload.case_reminder_defaults)));
    } catch (error) {
      console.error('Error fetching email settings:', error);
      setSnackbar({ open: true, message: 'Failed to load email settings', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const getCurrentSetting = (notificationType: string): EmailSetting => {
    const existingSetting = data?.settings.find(s => s.notification_type === notificationType);
    return existingSetting || {
      notification_type: notificationType,
      recipient_user_ids: [],
      is_active: true,
      config: notificationType === 'case_work_reminders' ? caseReminderDefaults : undefined,
    };
  };

  const saveSetting = async (
    notificationType: string,
    payload: { recipient_user_ids: number[]; is_active: boolean; config?: CaseReminderConfig }
  ) => {
    await api.put(`/email-settings/${notificationType}`, payload);
    setSnackbar({ open: true, message: 'Email settings updated successfully', severity: 'success' });
    await fetchEmailSettings();
  };

  const handleRecipientsChange = async (notificationType: string, recipientIds: number[]) => {
    if (!data) return;

    try {
      setSaving(notificationType);
      const currentSetting = getCurrentSetting(notificationType);
      await saveSetting(notificationType, {
        recipient_user_ids: recipientIds,
        is_active: currentSetting.is_active,
        ...(notificationType === 'case_work_reminders' ? { config: caseReminderDraft } : {}),
      });
    } catch (error: any) {
      console.error('Error updating email settings:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to update email settings',
        severity: 'error'
      });
    } finally {
      setSaving(null);
    }
  };

  const handleToggleActive = async (notificationType: string, isActive: boolean) => {
    if (!data) return;

    try {
      setSaving(notificationType);
      const currentSetting = getCurrentSetting(notificationType);
      await saveSetting(notificationType, {
        recipient_user_ids: currentSetting.recipient_user_ids,
        is_active: isActive,
        ...(notificationType === 'case_work_reminders' ? { config: caseReminderDraft } : {}),
      });
    } catch (error: any) {
      console.error('Error updating email settings:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to update email settings',
        severity: 'error'
      });
    } finally {
      setSaving(null);
    }
  };

  const handleSaveCaseReminderConfig = async () => {
    if (!data) return;

    try {
      setSaving('case_work_reminders');
      const currentSetting = getCurrentSetting('case_work_reminders');
      await saveSetting('case_work_reminders', {
        recipient_user_ids: currentSetting.recipient_user_ids,
        is_active: currentSetting.is_active,
        config: caseReminderDraft,
      });
    } catch (error: any) {
      console.error('Error updating case reminder settings:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to update case reminder settings',
        severity: 'error'
      });
    } finally {
      setSaving(null);
    }
  };

  const handleResetToDefault = async (notificationType: string) => {
    try {
      setSaving(notificationType);
      await api.post(`/email-settings/${notificationType}/reset`);

      if (notificationType === 'case_work_reminders') {
        setCaseReminderDraft(caseReminderDefaults);
      }

      setSnackbar({ open: true, message: 'Email settings reset to default (all PMP and Site administrators)', severity: 'success' });
      await fetchEmailSettings();
    } catch (error: any) {
      console.error('Error resetting email settings:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to reset email settings',
        severity: 'error'
      });
    } finally {
      setSaving(null);
    }
  };

  const renderCaseReminderSettings = (setting: EmailSetting) => {
    const disabled = saving === 'case_work_reminders' || !setting.is_active;

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
        <Typography variant="subtitle2">Reminder types</Typography>
        <Typography variant="caption" color="text.secondary">
          Choose which automated case reminders are sent by the daily job.
        </Typography>
        <FormGroup sx={{ pl: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={caseReminderDraft.reminder_types.due_soon}
                disabled={disabled}
                onChange={(e) => setCaseReminderDraft((prev) => ({
                  ...prev,
                  reminder_types: { ...prev.reminder_types, due_soon: e.target.checked },
                }))}
              />
            }
            label="Due soon — notify assignee before due date"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={caseReminderDraft.reminder_types.overdue}
                disabled={disabled}
                onChange={(e) => setCaseReminderDraft((prev) => ({
                  ...prev,
                  reminder_types: { ...prev.reminder_types, overdue: e.target.checked },
                }))}
              />
            }
            label="Overdue — notify assignee, creator, and firm managers after due date"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={caseReminderDraft.reminder_types.rush_unworked}
                disabled={disabled}
                onChange={(e) => setCaseReminderDraft((prev) => ({
                  ...prev,
                  reminder_types: { ...prev.reminder_types, rush_unworked: e.target.checked },
                }))}
              />
            }
            label="RUSH unworked — high priority case with no logged time"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={caseReminderDraft.reminder_types.response_expected}
                disabled={disabled}
                onChange={(e) => setCaseReminderDraft((prev) => ({
                  ...prev,
                  reminder_types: { ...prev.reminder_types, response_expected: e.target.checked },
                }))}
              />
            }
            label="Response expected — follow up when assignee has not replied to a comment"
          />
        </FormGroup>

        <Divider />

        <Typography variant="subtitle2">Timing</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
          <TextField
            label="Due soon (days before)"
            type="number"
            value={caseReminderDraft.due_soon_days}
            disabled={disabled}
            inputProps={{ min: 1, max: 30 }}
            onChange={(e) => setCaseReminderDraft((prev) => ({
              ...prev,
              due_soon_days: Math.max(1, Number(e.target.value) || 1),
            }))}
            fullWidth
          />
          <TextField
            label="RUSH unworked (hours)"
            type="number"
            value={caseReminderDraft.rush_unworked_hours}
            disabled={disabled}
            inputProps={{ min: 1, max: 168 }}
            onChange={(e) => setCaseReminderDraft((prev) => ({
              ...prev,
              rush_unworked_hours: Math.max(1, Number(e.target.value) || 1),
            }))}
            fullWidth
          />
          <TextField
            label="Response expected (hours)"
            type="number"
            value={caseReminderDraft.response_expected_hours}
            disabled={disabled}
            inputProps={{ min: 1, max: 168 }}
            onChange={(e) => setCaseReminderDraft((prev) => ({
              ...prev,
              response_expected_hours: Math.max(1, Number(e.target.value) || 1),
            }))}
            fullWidth
          />
        </Box>

        <Divider />

        <Typography variant="subtitle2">Additional recipients</Typography>
        <FormGroup sx={{ pl: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={caseReminderDraft.include_case_notifications_recipients}
                disabled={disabled}
                onChange={(e) => setCaseReminderDraft((prev) => ({
                  ...prev,
                  include_case_notifications_recipients: e.target.checked,
                }))}
              />
            }
            label="Also include Case Notifications recipients on all enabled reminder types"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={caseReminderDraft.admin_recipient_types.overdue}
                disabled={disabled}
                onChange={(e) => setCaseReminderDraft((prev) => ({
                  ...prev,
                  admin_recipient_types: { ...prev.admin_recipient_types, overdue: e.target.checked },
                }))}
              />
            }
            label="Include admin recipients below on overdue reminders"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={caseReminderDraft.admin_recipient_types.rush_unworked}
                disabled={disabled}
                onChange={(e) => setCaseReminderDraft((prev) => ({
                  ...prev,
                  admin_recipient_types: { ...prev.admin_recipient_types, rush_unworked: e.target.checked },
                }))}
              />
            }
            label="Include admin recipients below on RUSH unworked reminders"
          />
        </FormGroup>

        <Button
          variant="contained"
          onClick={handleSaveCaseReminderConfig}
          disabled={disabled}
          sx={{ alignSelf: 'flex-start' }}
        >
          Save reminder settings
        </Button>
      </Box>
    );
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
          <Typography>Loading email settings...</Typography>
        </Box>
      </Container>
    );
  }

  if (!isPmpAdmin()) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          You do not have permission to access this page. This section is only available to PMP Admins.
        </Alert>
      </Box>
    );
  }

  const getRecipients = (): NotificationRecipient[] => {
    if (!data) return [];
    return data.recipients?.length ? data.recipients : (data.pmp_admins || []);
  };

  const getRoleLabel = (role?: string) => {
    if (role === 'site_admin') return 'Site Admin';
    if (role === 'pmp_admin') return 'PMP Admin';
    return role?.replace(/_/g, ' ') || 'Admin';
  };

  if (!data) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
          <Typography color="error">Failed to load email settings</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Email color="primary" />
            <Typography variant="h4">Email Notification Settings</Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchEmailSettings}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="body1" color="textSecondary">
            Configure which PMP administrators and Site administrators should receive different types of email notifications.
            Case Reminders includes automated overdue, due-soon, RUSH, and response-expected alerts.
          </Typography>
        </Paper>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {Object.entries(data.notification_types).map(([notificationType, displayName]) => {
            const setting = getCurrentSetting(notificationType);
            const availableRecipients = getRecipients();
            const selectedRecipients = availableRecipients.filter((recipient) =>
              setting.recipient_user_ids.includes(recipient.id)
            );
            const isCaseReminders = notificationType === 'case_work_reminders';

            return (
              <Card key={notificationType} elevation={2}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        {displayName}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {isCaseReminders
                          ? 'Automated daily reminders for overdue and at-risk cases'
                          : `Notification Type: ${notificationType}`}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={setting.is_active}
                            onChange={(e) => handleToggleActive(notificationType, e.target.checked)}
                            disabled={saving === notificationType}
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {setting.is_active ? <NotificationsActive color="success" /> : <NotificationsOff color="disabled" />}
                            {setting.is_active ? 'Active' : 'Inactive'}
                          </Box>
                        }
                      />
                    </Box>
                  </Box>

                  {isCaseReminders && renderCaseReminderSettings(setting)}

                  <Divider sx={{ my: 2 }} />

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Typography variant="subtitle2">
                      {isCaseReminders ? 'Admin escalation recipients' : 'Recipients'} ({selectedRecipients.length} of {availableRecipients.length} administrators):
                    </Typography>
                    {isCaseReminders && (
                      <Typography variant="caption" color="text.secondary">
                        Used for overdue and RUSH unworked escalation when enabled above.
                      </Typography>
                    )}

                    <FormControl fullWidth disabled={saving === notificationType || !setting.is_active}>
                      <InputLabel>Select Recipients</InputLabel>
                      <Select
                        multiple
                        value={setting.recipient_user_ids}
                        onChange={(e) => handleRecipientsChange(notificationType, e.target.value as number[])}
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {(selected as number[]).map((recipientId) => {
                              const recipient = availableRecipients.find((r) => r.id === recipientId);
                              return recipient ? (
                                <Chip
                                  key={recipientId}
                                  label={`${recipient.name} (${getRoleLabel(recipient.role)})`}
                                  size="small"
                                  color="primary"
                                />
                              ) : null;
                            })}
                          </Box>
                        )}
                      >
                        {availableRecipients.map((recipient) => (
                          <MenuItem key={recipient.id} value={recipient.id}>
                            <Box>
                              <Typography variant="body2">
                                {recipient.name} — {getRoleLabel(recipient.role)}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {recipient.email}
                              </Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {selectedRecipients.length === 0 && setting.is_active && (
                      <Alert severity="warning">
                        No recipients selected. This notification type will fall back to all active PMP and Site administrators.
                      </Alert>
                    )}
                  </Box>
                </CardContent>

                <CardActions>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                    <Button
                      startIcon={<Refresh />}
                      onClick={() => handleResetToDefault(notificationType)}
                      disabled={saving === notificationType}
                    >
                      Reset to Default
                    </Button>
                    <Typography variant="caption" color="textSecondary" sx={{ alignSelf: 'center' }}>
                      {saving === notificationType ? 'Saving...' : isCaseReminders ? 'Recipients save automatically; use Save reminder settings for options above' : 'Changes are saved automatically'}
                    </Typography>
                  </Box>
                </CardActions>
              </Card>
            );
          })}
        </Box>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        >
          <Alert
            onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
};

export default EmailSettings;
